/**
 * useWorkspaceInvitations Hook - Paginated Invitation Management
 *
 * Custom hook for workspace invitation list with pagination and actions.
 *
 * @module hooks/useWorkspaceInvitations
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useDebounce } from './useDebounce';
import { apiClient } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import {
  WORKSPACE_DEFAULT_PAGE_SIZE,
  WORKSPACE_SEARCH_DEBOUNCE_MS,
} from '../constants/workspace';
import type {
  WorkspaceInvitation,
  InvitationStatus,
  WorkspaceRole,
} from '../types/workspace';
import type {
  GetWorkspaceInvitationsResponse,
  SendBatchInvitationsResponse,
  ResendInvitationResponse,
  CancelInvitationResponse,
  FailedInvitationResponse,
} from '../types/api/responses';
import type { InvitationRequestDto } from '../types/api/requests';

interface UseWorkspaceInvitationsParams {
  workspaceId: string | null;
  pageSize?: number;
  autoFetch?: boolean;
}

interface SendInvitationsResult {
  success: boolean;
  successCount: number;
  failureCount: number;
  failedInvitations?: FailedInvitationResponse[];
  error?: string;
}

interface UseWorkspaceInvitationsReturn {
  // State
  invitations: WorkspaceInvitation[];
  search: string;
  statusFilter: InvitationStatus | null;
  roleFilter: WorkspaceRole | null;
  totalCount: number;
  isLoading: boolean;
  hasLoaded: boolean;
  isSending: boolean;
  hasNextPage: boolean;
  error: string | null;

  // Actions
  setSearch: (search: string) => void;
  setStatusFilter: (status: InvitationStatus | null) => void;
  setRoleFilter: (role: WorkspaceRole | null) => void;
  loadMore: () => void;
  sendBatchInvitations: (
    invitations: InvitationRequestDto[]
  ) => Promise<SendInvitationsResult>;
  resendInvitation: (
    invitationId: string,
    newEmail?: string
  ) => Promise<boolean>;
  cancelInvitation: (invitationId: string) => Promise<boolean>;
  refresh: () => void;
}

/**
 * Hook for paginated workspace invitation management
 */
export function useWorkspaceInvitations({
  workspaceId,
  pageSize = WORKSPACE_DEFAULT_PAGE_SIZE,
  autoFetch = true,
}: UseWorkspaceInvitationsParams): UseWorkspaceInvitationsReturn {
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvitationStatus | null>(
    null
  );
  const [roleFilter, setRoleFilter] = useState<WorkspaceRole | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { debouncedValue: debouncedSearch } = useDebounce(
    search,
    WORKSPACE_SEARCH_DEBOUNCE_MS
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchInvitations = useCallback(
    async (pageNum: number, append = false) => {
      if (!workspaceId) {
        setInvitations([]);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: pageNum.toString(),
          pageSize: pageSize.toString(),
        });

        if (debouncedSearch) {
          params.set('search', debouncedSearch);
        }
        if (statusFilter) {
          params.set('status', statusFilter);
        }
        if (roleFilter) {
          params.set('role', roleFilter);
        }

        const response = await apiClient.get<GetWorkspaceInvitationsResponse>(
          `${API_ENDPOINTS.WORKSPACES.INVITATIONS(workspaceId)}?${params}`,
          { signal: abortControllerRef.current.signal }
        );

        const newInvitations = response.data.items;

        setInvitations(
          append ? (prev) => [...prev, ...newInvitations] : newInvitations
        );
        setTotalCount(response.data.totalCount);
        setHasNextPage(response.data.hasNextPage);
        setPage(pageNum);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          const message = err.message || 'Failed to fetch invitations';
          setError(message);
          console.error('Failed to fetch invitations:', err);
        }
      } finally {
        setHasLoaded(true);
        setIsLoading(false);
      }
    },
    [workspaceId, pageSize, debouncedSearch, statusFilter, roleFilter]
  );

  const loadMore = useCallback(() => {
    if (!isLoading && hasNextPage) {
      fetchInvitations(page + 1, true);
    }
  }, [isLoading, hasNextPage, page, fetchInvitations]);

  const sendBatchInvitations = useCallback(
    async (
      invitationsList: InvitationRequestDto[]
    ): Promise<SendInvitationsResult> => {
      if (!workspaceId) {
        return {
          success: false,
          successCount: 0,
          failureCount: 0,
          error: 'No workspace selected',
        };
      }

      setIsSending(true);
      setError(null);

      try {
        const response = await apiClient.post<SendBatchInvitationsResponse>(
          API_ENDPOINTS.WORKSPACES.INVITATIONS(workspaceId),
          { invitations: invitationsList }
        );

        // Refresh list after sending
        await fetchInvitations(1);

        return {
          success: response.data.failedInvitations.length === 0,
          successCount: response.data.invitationIds.length,
          failureCount: response.data.failedInvitations.length,
          failedInvitations: response.data.failedInvitations,
        };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to send invitations';
        setError(message);
        return {
          success: false,
          successCount: 0,
          failureCount: invitationsList.length,
          error: message,
        };
      } finally {
        setIsSending(false);
      }
    },
    [workspaceId, fetchInvitations]
  );

  const resendInvitation = useCallback(
    async (invitationId: string, newEmail?: string): Promise<boolean> => {
      if (!workspaceId) return false;

      try {
        await apiClient.post<ResendInvitationResponse>(
          API_ENDPOINTS.WORKSPACES.RESEND_INVITATION(workspaceId, invitationId),
          newEmail ? { newEmail } : {}
        );

        // Refresh list
        await fetchInvitations(1);

        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to resend invitation';
        setError(message);
        return false;
      }
    },
    [workspaceId, fetchInvitations]
  );

  const cancelInvitation = useCallback(
    async (invitationId: string): Promise<boolean> => {
      if (!workspaceId) return false;

      try {
        await apiClient.delete<CancelInvitationResponse>(
          API_ENDPOINTS.WORKSPACES.CANCEL_INVITATION(workspaceId, invitationId)
        );

        // Update local state
        setInvitations((prev) =>
          prev.filter((invitation) => invitation.id !== invitationId)
        );
        setTotalCount((prev) => prev - 1);

        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to cancel invitation';
        setError(message);
        return false;
      }
    },
    [workspaceId]
  );

  const refresh = useCallback(() => {
    setPage(1);
    fetchInvitations(1);
  }, [fetchInvitations]);

  useEffect(() => {
    if (autoFetch && workspaceId) {
      fetchInvitations(1);
    }
  }, [autoFetch, workspaceId, fetchInvitations]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    setInvitations([]);
    setSearch('');
    setStatusFilter(null);
    setRoleFilter(null);
    setPage(1);
    setTotalCount(0);
    setHasNextPage(false);
    setHasLoaded(false);
    setError(null);
  }, [workspaceId]);

  return {
    // State
    invitations,
    search,
    statusFilter,
    roleFilter,
    totalCount,
    isLoading,
    hasLoaded,
    isSending,
    hasNextPage,
    error,

    // Actions
    setSearch,
    setStatusFilter,
    setRoleFilter,
    loadMore,
    sendBatchInvitations,
    resendInvitation,
    cancelInvitation,
    refresh,
  };
}
